import { Store } from "@tanstack/store";
import { getYantrasRows } from "../../utils/transformDataToColumns";
import { IBet, IYantra } from "../../entities/yantras/yantras";
import { IYantrasColumn } from "../../entities/yantras/yantraCards";
import { addColumnCards, getCardsFromRows, setHistory } from "./cardsStoreHelpers";

export interface ICardsState {
  activeColumn: number[];
  cardIds: IBet;
  yantrasColumns: IYantrasColumn[];
  history: IRequestBody[];
  historyIndex: number | null;
  lastBet: IRequestBody;
  requestBody: IRequestBody;
  placedRows: string[];
}

export interface IRequestBody {
  images: Record<string, number>;
  rows: Record<string, number>;
}

const initialState: ICardsState = {
  activeColumn: [],
  cardIds: {},
  yantrasColumns: [],
  history: [],
  historyIndex: null,
  lastBet: {
    images: {},
    rows: {},
  },
  requestBody: {
    images: {},
    rows: {},
  },
  placedRows: [],
};

const cardsStore = new Store(initialState);

const updateYantrasRows = (yantras: IYantra[], rows: number, cols: number) => {
  cardsStore.setState((state) => ({
    ...state,
    yantrasColumns: getYantrasRows(yantras, rows, cols),
    cardNames: {},
  }));
};

const addColumn = (index: number) => {
  if (index == null) return;

  cardsStore.setState((state) => {
    const newState = { ...state };
    Object.assign(newState, addColumnCards(newState, index));

    const updatedRows = { ...state.requestBody.rows };
    const key = index.toString();
    updatedRows[key] = updatedRows[key] ? updatedRows[key] + 1 : 1;

    const column = newState.yantrasColumns?.[index];
    if (!column) return state;

    const updatedImages = { ...newState.requestBody.images };
    column.cards.forEach((card) => {
      delete updatedImages[card.id.toString()];
    });

    const rowCards: Record<string, number> = {};
    Object.keys(updatedRows).forEach((rowKey) => {
      newState.yantrasColumns[+rowKey]?.cards.forEach((card) => {
        rowCards[card.id] = updatedRows[rowKey];
      });
    });

    const finalCardIds = { ...updatedImages, ...rowCards };
    const reqBody = {
      images: updatedImages,
      rows: updatedRows,
    };

    const { newHistory, newHistoryIndex } = setHistory(state.history, reqBody);

    return {
      ...newState,
      cardIds: finalCardIds,
      activeColumn: [...(state.activeColumn ?? []), index],
      history: newHistory,
      historyIndex: newHistoryIndex,
      requestBody: reqBody,
    };
  });
};

const addActiveCard = (e: React.MouseEvent<HTMLElement>, id: string, executionId: string) => {
  if (!(e.target instanceof HTMLDivElement) || e.target.innerHTML.includes("Mui-disabled")) return;

  cardsStore.setState((state) => {
    const updatedCardIds = { ...state.cardIds };

    updatedCardIds[id] = updatedCardIds[id] ? updatedCardIds[id] + 1 : 1;

    console.debug(executionId);

    const updatedImages = { ...state.requestBody.images };
    updatedImages[id] = updatedImages[id] ? updatedImages[id] + 1 : 1;

    const reqBody = {
      images: { ...updatedImages },
      rows: { ...state.requestBody.rows },
    };

    const { newHistory, newHistoryIndex } = setHistory(state.history, reqBody);

    return {
      ...state,
      cardIds: updatedCardIds,
      history: newHistory,
      historyIndex: newHistoryIndex,
      requestBody: reqBody,
    };
  });
};

const addCardQuantity = (type: string, id: string, quantity: number) => {
  cardsStore.setState((state) => {
    const updatedImages = { ...state.requestBody.images };
    const updatedRows = { ...state.requestBody.rows };

    if (type === "image") {
      updatedImages[id] = quantity;
    } else {
      updatedRows[id] = quantity;
    }

    const reqBody = {
      images: { ...updatedImages },
      rows: { ...updatedRows },
    };
    const cardsFromRows = getCardsFromRows(updatedRows, state.yantrasColumns);
    const updatedCardIds = { ...updatedImages, ...cardsFromRows };
    const { newHistory, newHistoryIndex } = setHistory(state.history, reqBody);
    return {
      ...state,
      cardIds: updatedCardIds,
      history: newHistory,
      historyIndex: newHistoryIndex,
      requestBody: reqBody,
    };
  });
};

const clearBets = () => {
  if (Object.entries(cardsStore.state.cardIds).length === 0) return;
  cardsStore.setState((state) => {
    const reqBody = {
      images: {},
      rows: {},
    };
    const { newHistory, newHistoryIndex } = setHistory(state.history, reqBody);
    return {
      ...state,
      cardIds: {},
      activeColumn: [],
      history: newHistory,
      historyIndex: newHistoryIndex,
      requestBody: reqBody,
    };
  });
};

const clearHistory = () => {
  cardsStore.setState((state) => ({
    ...state,
    history: [],
    historyIndex: null,
  }));
};

const doubleBets = () => {
  if (Object.entries(cardsStore.state.cardIds).length === 0) return;
  cardsStore.setState((state) => {
    const updatedImages = { ...state.requestBody.images };
    const updatedRows = { ...state.requestBody.rows };
    const rowKeys = Object.keys(updatedRows);
    rowKeys.forEach((key) => {
      if (updatedRows[key]) {
        updatedRows[key] = updatedRows[key] * 2;
      }
    });
    for (let key in updatedImages) {
      updatedImages[key] *= 2;
    }

    const reqBody = {
      images: { ...updatedImages },
      rows: { ...updatedRows },
    };

    const cardsFromRows = getCardsFromRows(updatedRows, state.yantrasColumns);
    const updatedCardIds = { ...updatedImages, ...cardsFromRows };

    const { newHistory, newHistoryIndex } = setHistory(state.history, reqBody);

    return {
      ...state,
      cardIds: updatedCardIds,
      history: newHistory,
      historyIndex: newHistoryIndex,
      requestBody: reqBody,
    };
  });
};

const undoBets = () => {
  cardsStore.setState((state) => {
    const updatedCard: IRequestBody | {} =
      state.history.length && state.historyIndex !== null && state.historyIndex >= 1
        ? state.history[state.historyIndex - 1]
        : { images: {}, rows: {} };

    const reqBody = {
      images: { ...(updatedCard as IRequestBody).images },
      rows: { ...(updatedCard as IRequestBody).rows },
    };
    const cardsFromRows = getCardsFromRows(reqBody.rows, state.yantrasColumns);
    const updatedCardIds = { ...reqBody.images, ...cardsFromRows };
    return {
      ...state,
      cardIds: updatedCardIds,
      historyIndex: state.historyIndex && state.historyIndex >= 0 ? state.historyIndex - 1 : -1,
      activeColumn: [],
      requestBody: reqBody,
    };
  });
};

const redoBets = () => {
  cardsStore.setState((state) => {
    const updatedCard: IRequestBody | {} =
      state.historyIndex !== null
        ? state.history[state.historyIndex + 1]
        : { images: {}, rows: {} };

    const reqBody = {
      images: { ...(updatedCard as IRequestBody).images },
      rows: { ...(updatedCard as IRequestBody).rows },
    };

    const cardsFromRows = getCardsFromRows(reqBody.rows, state.yantrasColumns);
    const updatedCardIds = { ...reqBody.images, ...cardsFromRows };

    return {
      ...state,
      cardIds: updatedCardIds,
      historyIndex: state.historyIndex !== null ? state.historyIndex + 1 : null,
      activeColumn: [],
      requestBody: reqBody,
    };
  });
};

const rebet = () => {
  const { lastBet } = cardsStore.state;

  if (!Object.keys(lastBet.images).length && !Object.keys(lastBet.rows).length) return;

  cardsStore.setState((state) => {
    const reqBody = {
      images: { ...lastBet.images },
      rows: { ...lastBet.rows },
    };
    const cardsFromRows = getCardsFromRows(lastBet.rows, state.yantrasColumns);
    const updatedCardIds = { ...lastBet.images, ...cardsFromRows };

    const { newHistory, newHistoryIndex } = setHistory(state.history, reqBody);
    return {
      ...state,
      cardIds: updatedCardIds,
      history: newHistory,
      historyIndex: newHistoryIndex,
      requestBody: reqBody,
    };
  });
};

const resetBets = () => {
  cardsStore.setState((state) => {
    return {
      ...state,
      cardIds: {},
      history: [],
      historyIndex: null,
      activeColumn: [],
      placedRows: [],
      requestBody: {
        images: {},
        rows: {},
      },
    };
  });
};

const removeCard = (type: string, id: string) => {
  if (!type || !id) return;

  cardsStore.setState((state) => {
    const updatedImages = { ...state.requestBody.images };
    const updatedRows = { ...state.requestBody.rows };

    if (type === "image") {
      delete updatedImages[id];
    } else {
      delete updatedRows[id];
    }

    const reqBody = {
      images: { ...updatedImages },
      rows: { ...updatedRows },
    };
    const { newHistory, newHistoryIndex } = setHistory(state.history, reqBody);

    const cardsFromRows = getCardsFromRows(updatedRows, state.yantrasColumns);
    const updatedCardIds = { ...updatedImages, ...cardsFromRows };

    return {
      ...state,
      cardIds: updatedCardIds,
      history: newHistory,
      historyIndex: newHistoryIndex,
      requestBody: reqBody,
    };
  });
};

const setPlacedRows = (rowIndex: string[]) => {
  cardsStore.setState((state) => {
    const updatedRows = new Set([...state.placedRows, ...rowIndex]);
    return {
      ...state,
      placedRows: Array.from(updatedRows),
    };
  });
};

const clearPlacedRows = () => {
  cardsStore.setState((state) => ({
    ...state,
    placedRows: [],
  }));
};

const setLastBet = (lastBet: IRequestBody) => {
  cardsStore.setState((state) => ({
    ...state,
    lastBet,
  }));
};

export {
  cardsStore,
  addActiveCard,
  updateYantrasRows,
  addCardQuantity,
  clearBets,
  doubleBets,
  undoBets,
  redoBets,
  rebet,
  resetBets,
  removeCard,
  addColumn,
  setPlacedRows,
  clearHistory,
  setLastBet,
  clearPlacedRows,
};
